import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { Link, useLocation } from 'react-router'
import {
  HEADER_BAR_H,
  HEADER_CIRCLE,
  HEADER_CTA,
  HEADER_NAV_TEXT,
  HEADER_WORDMARK,
} from '../../../../components/site-header/sizes'
import { useCopy, useLocale } from '../../content'
import { SERVICE_KEYS } from '../../data/services'
import { matchRoute, normalizePath, pathFor, type RouteDef } from '../../routes'
import { LangSwitch } from './LangSwitch'
import { ServicesMenu } from './ServicesMenu'
import { ThemeToggle, BAR_COIN } from './ThemeToggle'

/** Scroll distance after which the home header slides in (Vercelli behaviour, spec §4.2). */
const REVEAL_AFTER_PX = 80
/** Tailwind `lg` — the full-screen menu only exists below it. */
const DESKTOP_QUERY = '(min-width: 1024px)'

/** Tab / Shift+Tab wrap inside the open full-screen menu, so focus never lands on the page hidden under it. */
function keepFocusInside(e: ReactKeyboardEvent<HTMLElement>) {
  if (e.key !== 'Tab') return
  const focusables = e.currentTarget.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')
  if (focusables.length === 0) return
  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault()
    last.focus()
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault()
    first.focus()
  }
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 12 12"
      className={`h-3 w-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
    >
      <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

/** Vercelli's logo + wordmark (the old site's markup). The wordmark hides below `sm`; the link then keeps its name from `aria-label`. */
function Brand() {
  return (
    <>
      <img
        src="/media/verde-vercelli/logo.png"
        alt=""
        aria-hidden="true"
        draggable={false}
        className="h-10 w-auto select-none pointer-events-none shrink-0 drop-shadow-[0_2px_8px_rgba(201,168,117,0.3)]"
      />
      <span className={`vc2-display ${HEADER_WORDMARK} hidden sm:inline`}>Verde Vercelli</span>
    </>
  )
}

/** Mobile-menu body coins (paper menu, both themes). */
const MENU_COIN = 'border-vc2-hairline bg-vc2-surface text-vc2-ink'

function MenuIcon({ open }: { open: boolean }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" className="h-5 w-5">
      {open ? (
        <path d="M5 5l10 10M15 5 5 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      ) : (
        <path d="M3 6h14M3 10h14M3 14h14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      )}
    </svg>
  )
}

/**
 * Fixed site header. On the home it stays hidden (and inert) until the page
 * has scrolled past 80 px, so the full-bleed hero reads uninterrupted; on every
 * other route it is visible from the first paint, prerendered HTML included.
 *
 * Hydration contract: the first render depends only on the pathname —
 * `scrolled`, `menuOpen`, `servicesOpen` and `servicesPrimed` all start `false`, and window,
 * document and matchMedia are touched only in effects and event handlers.
 * The ThemeToggle's first render is state-free too: its server snapshot is
 * `null` (no `aria-pressed`) through hydration, and CSS picks its icon from
 * the root class.
 *
 * Nav (spec §4.2 item 1): «Servizi ▾» · Chi siamo (`/#chi-siamo`) · Numeri
 * (`/#numeri`) · Zone servite · Contatti. The two fragment links point at the
 * home's sections from any page; SiteRoutes scrolls to the fragment after the
 * navigation. They are never `aria-current` (a section is not a page).
 *
 * The full-screen mobile menu is rendered as a SIBLING of `<header>`: the
 * header's transform (the slide) and the bar's backdrop-filter each make a
 * containing block for fixed descendants, which would shrink a `fixed inset-0`
 * child to the 64 px bar.
 */
export function Header() {
  const copy = useCopy()
  const locale = useLocale()
  const { pathname } = useLocation()
  const here = normalizePath(pathname)
  const isHome = matchRoute(pathname)?.route.kind === 'home'

  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [servicesOpen, setServicesOpen] = useState(false)
  const hidden = isHome && !scrolled

  // Any navigation closes both menus — adjusted during render (not in an
  // effect), so the next page never paints with a menu still open.
  const [seenPath, setSeenPath] = useState(pathname)
  if (seenPath !== pathname) {
    setSeenPath(pathname)
    setMenuOpen(false)
    setServicesOpen(false)
  }
  // A dropdown left open while the home header slides away must not reappear with it.
  if (hidden && servicesOpen) setServicesOpen(false)
  // The dropdown's preview photos load only once it has been opened or hovered (ServicesMenu).
  const [servicesPrimed, setServicesPrimed] = useState(false)
  if (servicesOpen && !servicesPrimed) setServicesPrimed(true)

  const servicesRef = useRef<HTMLDivElement>(null)
  const servicesButtonRef = useRef<HTMLButtonElement>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const menuCloseRef = useRef<HTMLButtonElement>(null)

  // Scroll position → reveal. Re-subscribed per route; the immediate read
  // also picks up a restored scroll position after a reload or back/forward.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > REVEAL_AFTER_PX)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [pathname])

  // Desktop services dropdown: Escape (focus back to its button) and a press outside close it.
  useEffect(() => {
    if (!servicesOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      const hadFocus = servicesRef.current?.contains(document.activeElement) ?? false
      setServicesOpen(false)
      if (hadFocus) servicesButtonRef.current?.focus()
    }
    const onPointer = (e: PointerEvent) => {
      if (!servicesRef.current?.contains(e.target as Node)) setServicesOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
    }
  }, [servicesOpen])

  // Full-screen menu: body scroll lock, initial focus, Escape, and closing when
  // the viewport grows past `lg` (the menu is display:none there, so a lock
  // left behind would freeze the page). The cleanup restores the previous
  // overflow on close, on navigation (which closes the menu) and on unmount.
  useEffect(() => {
    if (!menuOpen) return
    const body = document.body
    const prevOverflow = body.style.overflow
    body.style.overflow = 'hidden'
    menuCloseRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setMenuOpen(false)
      menuButtonRef.current?.focus()
    }
    const desktop = window.matchMedia(DESKTOP_QUERY)
    const onViewport = () => {
      if (desktop.matches) setMenuOpen(false)
    }
    document.addEventListener('keydown', onKey)
    desktop.addEventListener('change', onViewport)
    return () => {
      body.style.overflow = prevOverflow
      document.removeEventListener('keydown', onKey)
      desktop.removeEventListener('change', onViewport)
    }
  }, [menuOpen])

  const current = (to: string) => (here === to ? 'page' : undefined)
  const closeMenu = () => setMenuOpen(false)
  const closeMenuAndRefocus = () => {
    setMenuOpen(false)
    menuButtonRef.current?.focus()
  }

  const homePath = pathFor('home', locale)
  const contactPath = pathFor('contatti', locale)
  const aboutPath = locale === 'it' ? '/#chi-siamo' : '/en/#chi-siamo'
  const numbersPath = locale === 'it' ? '/#numeri' : '/en/#numeri'
  const topLinks: readonly [RouteDef['key'] | 'about' | 'numbers', string, string][] = [
    ['about', copy.nav.about, aboutPath],
    ['numbers', copy.nav.numbers, numbersPath],
    ['zone', copy.nav.areas, pathFor('zone', locale)],
    ['contatti', copy.nav.contact, contactPath],
  ]

  // Current page on paper (services panel, mobile menu): full-contrast ink text
  // + an accent underline (the decoration may be low-contrast).
  const currentLink =
    'underline-offset-4 aria-[current=page]:text-vc2-ink aria-[current=page]:underline aria-[current=page]:decoration-vc2-accent'
  // On the forest-dark bar (index.css :root `--brand-*`, both themes — the old
  // Vercelli header's palette): cream text at 70 %, full on hover and on the
  // current page, which is also underlined.
  const barCurrent =
    'underline-offset-4 aria-[current=page]:text-brand-cream aria-[current=page]:underline aria-[current=page]:decoration-brand-cream/60'
  const navLink = `text-brand-cream/70 transition-colors hover:text-brand-cream ${barCurrent}`
  const cta = `${HEADER_CTA} bg-vc2-cta text-vc2-cta-fg transition-opacity hover:opacity-90`
  // The bar's CTA: the old Vercelli pill, cream with forest-dark text, brass on hover.
  const barCta = `${HEADER_CTA} bg-brand-cream text-brand-forest-dark hover:bg-brand-brass-bright transition-all duration-300 shadow-lg shadow-black/20`
  // Round buttons on the bar are paper coins (cream on the forest-dark bar).
  const coin = BAR_COIN

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-40 transition-[transform,opacity] duration-500 motion-reduce:transition-none ${
          hidden ? 'pointer-events-none -translate-y-full opacity-0' : 'translate-y-0 opacity-100'
        }`}
        aria-hidden={hidden || undefined}
        inert={hidden}
      >
        <div className="border-b border-brand-cream/10 bg-brand-forest-dark/85 text-brand-cream backdrop-blur-xl">
          <div className={`vc2-container flex ${HEADER_BAR_H} items-center justify-between gap-4 xl:gap-6`}>
            <Link
              to={homePath}
              aria-current={current(homePath)}
              aria-label="Verde Vercelli"
              className="flex items-center gap-2.5 sm:gap-3"
            >
              <Brand />
            </Link>

            <nav
              aria-label={copy.a11y.mainNav}
              className={`hidden items-center gap-5 whitespace-nowrap ${HEADER_NAV_TEXT} lg:flex xl:gap-7`}
            >
              <div
                ref={servicesRef}
                className="group relative"
                onPointerEnter={() => setServicesPrimed(true)}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setServicesOpen(false)
                }}
              >
                <button
                  ref={servicesButtonRef}
                  type="button"
                  aria-haspopup="true"
                  aria-expanded={servicesOpen}
                  aria-controls="vc2-services"
                  onClick={() => setServicesOpen((o) => !o)}
                  className="inline-flex items-center gap-1.5 text-brand-cream/70 transition-colors hover:text-brand-cream"
                >
                  {copy.nav.services}
                  <ChevronIcon open={servicesOpen} />
                </button>
                <div className={`absolute left-0 top-full pt-3 group-hover:block ${servicesOpen ? 'block' : 'hidden'}`}>
                  <ServicesMenu
                    primed={servicesPrimed}
                    onNavigate={() => setServicesOpen(false)}
                    currentLinkClass={currentLink}
                  />
                </div>
              </div>
              {topLinks.map(([key, label, to]) => (
                <Link key={key} to={to} aria-current={current(to)} className={navLink}>
                  {label}
                </Link>
              ))}
            </nav>

            {/* Right cluster: CTA (from `lg`) · language button · theme toggle, then the
                hamburger below `lg`. The two round buttons stay together at the right
                edge (owner, 2026-09-24). */}
            <div className="flex items-center gap-2 lg:gap-3">
              <div className="hidden lg:flex">
                <Link to={contactPath} className={barCta}>
                  {copy.nav.cta}
                </Link>
              </div>

              <LangSwitch />
              <ThemeToggle />

              <button
                ref={menuButtonRef}
                type="button"
                aria-expanded={menuOpen}
                aria-controls="vc2-menu"
                aria-label={menuOpen ? copy.a11y.closeMenu : copy.a11y.openMenu}
                onClick={() => setMenuOpen((o) => !o)}
                className={`${HEADER_CIRCLE} ${coin} lg:hidden`}
              >
                <MenuIcon open={menuOpen} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div
          id="vc2-menu"
          role="dialog"
          aria-modal="true"
          aria-label={copy.a11y.mainNav}
          onKeyDown={keepFocusInside}
          className="fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-vc2-paper text-vc2-ink lg:hidden"
        >
          {/* Top strip = the forest-dark bar, so opening the menu does not flash the bar's colour away. */}
          <div className="border-b border-brand-cream/10 bg-brand-forest-dark text-brand-cream">
            <div className={`vc2-container flex ${HEADER_BAR_H} items-center justify-between gap-6`}>
              <Link
                to={homePath}
                aria-current={current(homePath)}
                onClick={closeMenu}
                aria-label="Verde Vercelli"
                className="flex items-center gap-2.5 sm:gap-3"
              >
                <Brand />
              </Link>
              <button
                ref={menuCloseRef}
                type="button"
                aria-label={copy.a11y.closeMenu}
                onClick={closeMenuAndRefocus}
                className={`${HEADER_CIRCLE} ${coin}`}
              >
                <MenuIcon open />
              </button>
            </div>
          </div>

          <nav aria-label={copy.a11y.mainNav} className="vc2-container py-8">
            <p className="vc2-kicker text-vc2-ink-soft">{copy.nav.services}</p>
            <ul className="mt-3 divide-y divide-vc2-hairline border-y border-vc2-hairline">
              {SERVICE_KEYS.map((k) => {
                const to = pathFor(k, locale)
                return (
                  <li key={k}>
                    <Link
                      to={to}
                      aria-current={current(to)}
                      onClick={closeMenu}
                      className={`vc2-display block py-3 text-2xl ${currentLink}`}
                    >
                      {copy.pages[k].name}
                    </Link>
                  </li>
                )
              })}
            </ul>
            <ul className="mt-8 space-y-3">
              {topLinks.map(([key, label, to]) => (
                <li key={key}>
                  <Link
                    to={to}
                    aria-current={current(to)}
                    onClick={closeMenu}
                    className={`vc2-display block text-2xl ${currentLink}`}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Same order as the bar: CTA · language · theme, the round pair at the right. */}
          <div className="vc2-container flex items-center justify-between gap-2 pb-12">
            <Link to={contactPath} onClick={closeMenu} className={cta}>
              {copy.nav.cta}
            </Link>
            <div className="flex items-center gap-2">
              <LangSwitch className={MENU_COIN} />
              <ThemeToggle className={MENU_COIN} />
            </div>
          </div>
        </div>
      )}
    </>
  )
}
